import { useState } from 'react';
import { Play } from 'lucide-react';
import { useBulkEnqueueAgentRuns } from '../../api/useAdminOps';
import { AI_STAGE_TASKS, STAGE_SHORT } from './opsUi';
import { AdminAPI } from '@shared/types/api';

// Run one pipeline stage for every neighborhood participating in an event
// (across all its cities) in one click — with an "only if not yet run"
// guard (default on) that skips neighborhoods where the stage already
// completed, or is currently queued/running.

const OUTCOME_LABEL: Record<AdminAPI.BulkEnqueueOutcome, string> = {
  queued: 'queued',
  existing: 'rerun requested',
  duplicate: 'duplicate',
  disabled: 'automation off',
  capped: 'run cap reached',
  skipped_already_ran: 'already ran',
  skipped_in_progress: 'in progress',
};

export default function BulkStageRunControl({
  eventTimeId,
  eventType,
}: {
  eventTimeId: string;
  eventType?: string;
}) {
  const [task, setTask] = useState<AdminAPI.AgentTaskType>('match.reoptimize');
  const [onlyIfNotYetRun, setOnlyIfNotYetRun] = useState(true);
  const bulkRun = useBulkEnqueueAgentRuns();

  return (
    <div className="flex items-center gap-2 flex-wrap text-xs">
      <select
        value={task}
        onChange={(e) => setTask(e.target.value as AdminAPI.AgentTaskType)}
        className="px-2 py-1 rounded-md border border-neutral-300 dark:border-neutral-600 bg-transparent text-xs"
        title="Pipeline stage to run for every neighborhood in this event"
      >
        {AI_STAGE_TASKS.filter(
          // Reservation-free event types (coffee, drinks) have no reserve stages.
          (t) => AdminAPI.eventTypeRequiresReservations(eventType) || !t.startsWith('reserve.')
        ).map((t) => (
          <option key={t} value={t}>
            {STAGE_SHORT[t] ?? t}
          </option>
        ))}
      </select>
      <label className="inline-flex items-center gap-1 text-dark-muted cursor-pointer select-none">
        <input
          type="checkbox"
          checked={onlyIfNotYetRun}
          onChange={(e) => setOnlyIfNotYetRun(e.target.checked)}
          className="rounded"
        />
        only if not yet run
      </label>
      <button
        onClick={() => bulkRun.mutate({ eventTimeId, taskType: task, onlyIfNotYetRun })}
        disabled={bulkRun.isPending}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary-500 text-white font-medium hover:bg-primary-600 disabled:opacity-50"
        title="Run this stage for every neighborhood in this event"
      >
        <Play className="w-3 h-3" />
        {bulkRun.isPending ? 'Running…' : 'Run for all neighborhoods'}
      </button>
      {bulkRun.data?.data && (
        <span className="text-dark-muted">
          {bulkRun.data.message}
          {' — '}
          {bulkRun.data.data.results
            .map((r) => `${r.neighborhoodName}: ${OUTCOME_LABEL[r.outcome]}`)
            .join(', ')}
        </span>
      )}
      {bulkRun.isError && (
        <span className="text-red-600">
          Failed: {(bulkRun.error as Error)?.message ?? 'unknown error'}
        </span>
      )}
    </div>
  );
}
